/**
 * Catálogo e layout do simulador VIP — itens gerados a partir de images/icons/{pasta}/.
 */
import { translate } from "@/i18n/I18nProvider";
import { buildCatalogFromManifest, getManifestFolderNames } from "./buildCatalogFromIcons";
import { armorGhostIconUrl, iconAssetUrl } from "./iconAssets";
import { ARMOR_SLOT_ORDER, OUTFIT_ARMOR_SLOT } from "./vipArmorSlots";
import {
    allArmorSlotsFull,
    canPlaceClothingInSlot,
    findFirstFreeArmorSlot,
    getClothingPlacementBlockReason,
    hasOutfitEquippedInArmor,
    isArmorClothingEntry,
    isOutfitEntry,
} from "./vipClothingRules";

export { ARMOR_SLOT_ORDER, OUTFIT_ARMOR_SLOT };
import { createEmptyWeaponAttachments } from "./weaponAttachments";

export { iconAssetUrl };

/** @deprecated Usar iconAssetUrl — mantido para referências antigas. */
export function vipAssetUrl(folder, file) {
    return iconAssetUrl(folder, file);
}

/** Categorias de item que preferem o cinto (hotbar) antes do inventário. */
export const HOTBAR_PREFERRED_CATS = new Set(["weapon", "ammo", "consumable"]);

/**
 * Calcula o destino automático ao clicar num item da lista (estilo inventário Rust).
 * @param {{ cat: string }} entry
 * @param {{ inv: unknown[], hotbar: unknown[], armor: Record<string, unknown> }} sim
 */
export function resolveAutoPlaceTarget(entry, sim) {
    if (!entry?.cat) return null;

    if (isOutfitEntry(entry)) {
        const invIdx = sim.inv.findIndex((s) => !s);
        if (invIdx >= 0) return { type: "slot", zone: "inv", index: invIdx };
        const hotIdx = sim.hotbar.findIndex((s) => !s);
        if (hotIdx >= 0) return { type: "slot", zone: "hotbar", index: hotIdx };
        if (
            !sim.armor[OUTFIT_ARMOR_SLOT] &&
            canPlaceClothingInSlot(sim, "armor", OUTFIT_ARMOR_SLOT, entry)
        ) {
            return { type: "slot", zone: "armor", accept: OUTFIT_ARMOR_SLOT };
        }
        return null;
    }

    if (isArmorClothingEntry(entry)) {
        const accept = findFirstFreeArmorSlot(sim, entry);
        if (!accept) return null;
        return { type: "slot", zone: "armor", accept };
    }

    if (entry.cat === "attachment") {
        const invIdx = sim.inv.findIndex((s) => !s);
        if (invIdx >= 0) return { type: "slot", zone: "inv", index: invIdx };
        return null;
    }

    if (HOTBAR_PREFERRED_CATS.has(entry.cat)) {
        const hotIdx = sim.hotbar.findIndex((s) => !s);
        if (hotIdx >= 0) return { type: "slot", zone: "hotbar", index: hotIdx };
        const invIdx = sim.inv.findIndex((s) => !s);
        if (invIdx >= 0) return { type: "slot", zone: "inv", index: invIdx };
        return null;
    }

    const invIdx = sim.inv.findIndex((s) => !s);
    if (invIdx >= 0) return { type: "slot", zone: "inv", index: invIdx };
    const hotIdx = sim.hotbar.findIndex((s) => !s);
    if (hotIdx >= 0) return { type: "slot", zone: "hotbar", index: hotIdx };
    return null;
}

/**
 * Mensagem quando resolveAutoPlaceTarget devolve null.
 * @param {{ cat?: string, name?: string }} entry
 * @param {{ inv: unknown[], hotbar: unknown[], armor: Record<string, unknown> }} sim
 * @returns {string}
 */
export function getAutoPlaceFailureReason(entry, sim) {
    if (!entry) return translate("simulator.place.generic");

    if (isArmorClothingEntry(entry)) {
        if (hasOutfitEquippedInArmor(sim)) {
            return translate("simulator.place.outfitEquipped");
        }
        if (allArmorSlotsFull(sim)) {
            return translate("simulator.place.armorFull");
        }
        return translate("simulator.place.noArmorSlot");
    }

    if (isOutfitEntry(entry)) {
        const block = getClothingPlacementBlockReason(sim, "armor", OUTFIT_ARMOR_SLOT, entry);
        if (block) return block;
        const invFull = sim.inv.every(Boolean);
        const hotFull = sim.hotbar.every(Boolean);
        if (invFull && hotFull && sim.armor[OUTFIT_ARMOR_SLOT]) {
            return translate("simulator.place.fullTorsoTaken");
        }
        if (invFull && hotFull) {
            return translate("simulator.place.fullWearOutfit");
        }
        return translate("simulator.place.noOutfitSlot");
    }

    const invFull = sim.inv.every(Boolean);
    const hotFull = sim.hotbar.every(Boolean);
    if (invFull && hotFull) {
        return translate("simulator.place.invBeltFull");
    }
    return translate("simulator.place.kitFull");
}

/** Categorias = nomes das pastas em images/icons/ (ex.: Armas, Munição, Outros). `label` = nome PT. */
export const ITEM_CATEGORIES = getManifestFolderNames().map((name) => ({
    key: name,
    label: name,
}));

/** Rótulos PT (chave → nome da pasta). Para o idioma atual usar `getCategoryLabel`. */
export const CATEGORY_LABELS = ITEM_CATEGORIES.reduce((acc, row) => {
    acc[row.key] = row.label;
    return acc;
}, {});

/**
 * Rótulo da categoria no idioma atual (ou `lang`).
 * @param {string} key — nome da pasta / categoria PT
 * @param {"pt"|"en"} [lang]
 */
export function getCategoryLabel(key, lang) {
    const map = translate("simulator.categories", null, lang);
    return (map && typeof map === "object" && map[key]) || CATEGORY_LABELS[key] || key;
}

const VALID_CATEGORY_KEYS = new Set(ITEM_CATEGORIES.map((c) => c.key));
const FALLBACK_CATEGORY = ITEM_CATEGORIES.find((c) => c.key === "Outros")?.key ?? ITEM_CATEGORIES[0]?.key ?? "Outros";

export function normalizeItemCategory(key) {
    if (key && VALID_CATEGORY_KEYS.has(key)) return key;
    return FALLBACK_CATEGORY;
}

export const CATALOG = buildCatalogFromManifest();

/** Fantasmas dos slots de equipamento — ficheiros em `images/icones/`. */
export const ARMOR_LAYOUT = [
    { accept: "armor-head", ghost: armorGhostIconUrl("hoodie.png") },
    { accept: "armor-mask", ghost: armorGhostIconUrl("facemask.png") },
    { accept: "armor-chest", ghost: armorGhostIconUrl("chestplate.png") },
    {
        accept: "armor-belt",
        ghost: armorGhostIconUrl("kilt.png"),
        ghostClass: "vip-sim-armor-ghost--belt",
    },
    { accept: "armor-hands", ghost: armorGhostIconUrl("gloves.png") },
    { accept: "armor-legs", ghost: armorGhostIconUrl("jeans.png") },
    { accept: "armor-feet", ghost: armorGhostIconUrl("boots.png") },
];

/** Apenas acessibilidade — slots sem etiqueta visível. */
export function getArmorSlotAriaLabel(accept, lang) {
    if (ARMOR_SLOT_ORDER.includes(accept)) return translate(`simulator.armorSlot.${accept}`, null, lang);
    return translate("simulator.armorSlot.default", null, lang);
}
/** @deprecated — usar vipBenefitsConfig.js */
export { BENEFIT_DEFINITIONS as BENEFITS } from "./vipBenefitsConfig";

export function getCatalogEntry(catalogId) {
    return CATALOG.find((e) => e.id === catalogId) ?? null;
}

export function snapQuantityForEntry(entry, raw) {
    if (!entry.quantityEnabled) return 1;
    const min = entry.min ?? 1;
    const max = entry.max ?? 99999999;
    const step = entry.step ?? 1;
    let q = Math.floor(Number(raw));
    if (!Number.isFinite(q)) return min;
    q = Math.max(min, Math.min(max, q));
    const k = Math.round((q - min) / step);
    let snapped = min + k * step;
    if (snapped > max) snapped = min + Math.floor((max - min) / step) * step;
    if (snapped < min) snapped = min;
    return snapped;
}

function newInstanceId() {
    return typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createPlacementFromCatalogEntry(entry) {
    const qty = snapQuantityForEntry(entry, entry.quantity ?? entry.defaultQty ?? 1);
    const placement = { instanceId: newInstanceId(), catalogId: entry.id, quantity: qty };
    if (entry.cat === "weapon" && entry.weaponKey) {
        placement.attachments = createEmptyWeaponAttachments(entry.weaponKey);
    }
    return placement;
}

/** Modelo fixo em «Itens disponíveis» — não sai do pool ao adicionar ao kit. */
export function createPoolPlacementFromCatalogEntry(entry) {
    const placement = createPlacementFromCatalogEntry(entry);
    placement.instanceId = `pool:${entry.id}`;
    return placement;
}

/** Cópia para o inventário / cinto / equipamento (novo instanceId). */
export function createKitPlacementFromTemplate(template) {
    const entry = getCatalogEntry(template.catalogId);
    if (!entry) return null;
    const placement = {
        instanceId: newInstanceId(),
        catalogId: template.catalogId,
        quantity: snapQuantityForEntry(entry, template.quantity),
    };
    if (entry.cat === "weapon" && entry.weaponKey) {
        placement.attachments = createEmptyWeaponAttachments(entry.weaponKey);
    }
    return placement;
}

export function buildInitialPool() {
    const pool = {};
    ITEM_CATEGORIES.forEach((c) => {
        pool[c.key] = [];
    });
    CATALOG.forEach((entry) => {
        const cat = normalizeItemCategory(entry.category || "misc");
        pool[cat].push(createPoolPlacementFromCatalogEntry(entry));
    });
    return pool;
}
